import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import internationalTransferService from '../services/internationalTransferService';
import Card from '../components/common/Card';

const VerificationStatus = () => {
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      if (!refreshing) setLoading(true);
      const status = await internationalTransferService.getVerificationStatus();
      setVerificationStatus(status);
      setError('');
    } catch (error) {
      console.error('Error fetching verification status:', error);
      setError('Failed to load verification status. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchVerificationStatus();
  };

  const handleStartVerification = () => {
    navigate('/transfer/international/verification');
  };

  const handleMakeTransfer = () => {
    navigate('/transfer');
  };

  const handleCheckCodeInfo = async () => {
    try {
      const codeInfo = await internationalTransferService.getCurrentVerificationCodeInfo();
      if (codeInfo.has_active_code) {
        alert(`You have an active code for Step ${codeInfo.step}. Expires: ${new Date(codeInfo.expires_at).toLocaleString()}`);
      } else {
        alert('No active verification code. Please ask bank staff to generate one.');
      }
    } catch (error) {
      console.error('Error fetching code info:', error);
      alert('Unable to fetch code information.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading verification status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-neutral-900 mb-2">
              Account Verification Status
            </h1>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="mt-4 text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center mx-auto"
            >
              <svg className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6">
              {error}
            </div>
          )}

          {verificationStatus && (
            <>
              {/* Status Overview Card */}
              <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-8 mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                  <div className="mb-6 md:mb-0">
                    <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                      {verificationStatus.is_verification_completed ? (
                        <span className="text-green-600 flex items-center">
                          <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          🎉 Account Fully Verified
                        </span>
                      ) : (
                        <span className="text-primary-600 flex items-center">
                          <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          Verification In Progress
                        </span>
                      )}
                    </h2>
                    <p className="text-neutral-600">
                      {verificationStatus.is_verification_completed
                        ? 'Your account is fully verified! You can make domestic and international transfers without verification codes.'
                        : ``}
                    </p>
                  </div>
                </div>
                
              </Card>

              {/* Action Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {!verificationStatus.is_verification_completed ? (
                  <>
                    {/* Start Verification */}
                    <Card className="bg-gradient-to-br from-primary-50 to-accent-50 border border-primary-200 rounded-xl p-6">
                      <button
                        onClick={handleStartVerification}
                        className="w-full bg-gradient-to-r from-primary-600 to-accent-600 text-white py-3 rounded-xl hover:from-primary-700 hover:to-accent-700 transition font-semibold"
                      >
                        Enter Verification Code
                      </button>
                    </Card>
                  </>
                ) : (
                  <>
                    {/* Verified - Make Transfer */}
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-bold text-green-900">🎉 Fully Verified!</h3>
                          <p className="text-sm text-green-600">No more verification needed</p>
                        </div>
                      </div>
                      <p className="text-green-700 mb-4">
                        Your account is now fully verified. You can make transfers without any verification codes!
                      </p>
                      <button
                        onClick={handleMakeTransfer}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl hover:from-green-600 hover:to-emerald-600 transition font-semibold"
                      >
                        Make a Transfer
                      </button>
                    </Card>
                  </>
                )}
              </div>

              {/* Important Information */}
              <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6">
                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <h4 className="font-semibold text-neutral-900 mb-3">Need Help?</h4>
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <p className="text-sm text-neutral-600 mb-4 md:mb-0">
                      Contact your bank admin for verification code and assistance.
                    </p>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-neutral-700">
                        📧 Email: omegatrbnk01@aol.com
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationStatus;
